var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import * as echarts from "echarts/core";
import { getChartColor, LEGEND_TEXT_COLOR, AXIS_TEXT_COLOR, DIMMED_OPACITY, DEAMS_DIMMED_OPACITY, DEAMS_FOCUS_DIMMED_OPACITY, } from "../utils/chartConstants";
import { formatNumericValue, getChartDataset, LINE_CHART_SMOOTH_INTENSITY, parseNumericValue, resolveMultiSeriesKeys, } from "../../../utils/chartData";
import { buildBarValueLabel, getPaddedAxisMax } from "../utils/barValueLabel";
export function buildMultiSeriesOption(data, baseType, isDeamsSeries, isStackedBar, isClusteredBar, selectedSeries, useCustomLegendList, isMobile, formatPeriodoLabel, formatPeriodoTooltipLabel, buildDeamsYAxisTicks, chartTitle) {
    var dataset = getChartDataset(data);
    var multiKeys = resolveMultiSeriesKeys(data, dataset);
    if (!multiKeys)
        return null;
    var xKey = multiKeys.xKey, seriesKey = multiKeys.seriesKey, numericKey = multiKeys.valueKey;
    var xValues = Array.from(new Set(dataset.map(function (item) { return String(item[xKey]); })));
    var rawSeriesNames = Array.from(new Set(dataset.map(function (item) { return String(item[seriesKey]); })));
    var normalizedChartTitle = (chartTitle !== null && chartTitle !== void 0 ? chartTitle : "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();
    var isFaixaEtariaRacaCorChart = normalizedChartTitle === "faixa etaria x raca/cor";
    var seriesTotals = rawSeriesNames.reduce(function (acc, name) {
        acc[name] = dataset
            .filter(function (item) { return String(item[seriesKey]) === name; })
            .reduce(function (sum, item) { return sum + parseNumericValue(item[numericKey]); }, 0);
        return acc;
    }, {});
    var seriesNames = isFaixaEtariaRacaCorChart
        ? __spreadArray([], rawSeriesNames, true).sort(function (a, b) { return seriesTotals[b] - seriesTotals[a]; })
        : rawSeriesNames;
    var _a = buildDeamsYAxisTicks(dataset), yAxisMax = _a.yAxisMax, yAxisStep = _a.yAxisStep, ticks = _a.ticks;
    var multiTooltip = {
        trigger: "axis",
        backgroundColor: "rgba(255,255,255,0.96)",
        borderColor: "#E5E7EB",
        borderWidth: 1,
        textStyle: {
            fontFamily: "Montserrat, sans-serif",
            color: "#374151",
            fontSize: 13,
        },
        extraCssText: "box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12); border-radius: 8px;",
        formatter: function (params) {
            var _a, _b, _c, _d;
            if (!isDeamsSeries)
                return tooltipFormatter(params);
            if (!Array.isArray(params) || params.length === 0) {
                return tooltipFormatter(params);
            }
            var axisLabel = formatPeriodoTooltipLabel(String((_d = (_b = (_a = params[0]) === null || _a === void 0 ? void 0 : _a.axisValueLabel) !== null && _b !== void 0 ? _b : (_c = params[0]) === null || _c === void 0 ? void 0 : _c.name) !== null && _d !== void 0 ? _d : ""));
            var rows = params.map(function (item) {
                var _a, _b, _c;
                var matchingRow = dataset.find(function (row) {
                    var _a, _b, _c, _d;
                    return String(row[xKey]) ===
                        String((_b = (_a = item.axisValueLabel) !== null && _a !== void 0 ? _a : item.name) !== null && _b !== void 0 ? _b : "") &&
                        String(row[seriesKey]) ===
                            String((_d = (_c = item.seriesName) !== null && _c !== void 0 ? _c : item.name) !== null && _d !== void 0 ? _d : "");
                });
                var marker = (_a = item.marker) !== null && _a !== void 0 ? _a : "";
                var seriesName = (_c = (_b = item.seriesName) !== null && _b !== void 0 ? _b : item.name) !== null && _c !== void 0 ? _c : "";
                var territorio = matchingRow === null || matchingRow === void 0 ? void 0 : matchingRow.territorio;
                var delegacia = matchingRow === null || matchingRow === void 0 ? void 0 : matchingRow.delegacia;
                var label = delegacia
                    ? "".concat(seriesName, " <span style=\"color:#6B7280\">(").concat(territorio, ")</span>")
                    : seriesName;
                return "".concat(marker).concat(label, ": <strong>").concat(formatNumericValue(item.value), "</strong>");
            });
            return "<strong>".concat(axisLabel, "</strong><br/>").concat(rows.join("<br/>"));
        },
    };
    var hasWebLegendSelection = !isDeamsSeries && !useCustomLegendList && selectedSeries.length > 0;
    var legendTextStyleKeys = seriesNames.reduce(function (acc, name, index) {
        acc[name] = "legendText".concat(index);
        return acc;
    }, {});
    var legendMarkerStyleKeys = seriesNames.reduce(function (acc, name, index) {
        acc[name] = "legendMarker".concat(index);
        return acc;
    }, {});
    var legendRichStyles = seriesNames.reduce(function (acc, name, index) {
        var color = getChartColor(name, index);
        var isActive = selectedSeries.length === 0 || selectedSeries.includes(name);
        acc[legendTextStyleKeys[name]] = {
            color: hasWebLegendSelection && !isActive ? "#9CA3AF" : color,
            fontWeight: hasWebLegendSelection && isActive ? 700 : 500,
        };
        acc[legendMarkerStyleKeys[name]] = {
            color: hasWebLegendSelection && !isActive ? "#D1D5DB" : color,
            fontWeight: 700,
            fontSize: isMobile ? 11 : 12,
            padding: [0, 6, 0, 0],
        };
        return acc;
    }, {});
    var legendFormatter = function (name) {
        var textStyleKey = legendTextStyleKeys[name];
        var markerStyleKey = legendMarkerStyleKeys[name];
        return textStyleKey && markerStyleKey
            ? "{".concat(markerStyleKey, "|\u25CF}{").concat(textStyleKey, "|").concat(name, "}")
            : name;
    };
    var multiSeries = seriesNames.map(function (name, i) {
        var _a;
        var color = getChartColor(name, i);
        var dataPoints = xValues.map(function (x) {
            var row = dataset.find(function (item) { return String(item[xKey]) === x && String(item[seriesKey]) === name; });
            return row
                ? parseNumericValue(row[numericKey])
                : baseType === "line"
                    ? null
                    : 0;
        });
        if (baseType === "line") {
            var isActive = selectedSeries.length === 0 || selectedSeries.includes(name);
            var isFocusedSingle = selectedSeries.length === 1 && selectedSeries[0] === name;
            var hasSingleVisibleSeries = isFocusedSingle ||
                (selectedSeries.length === 0 && seriesNames.length === 1);
            var inactiveOpacity = isDeamsSeries
                ? DEAMS_FOCUS_DIMMED_OPACITY
                : DIMMED_OPACITY;
            return {
                name: name,
                type: "line",
                color: color,
                smooth: LINE_CHART_SMOOTH_INTENSITY,
                symbol: "circle",
                symbolSize: isDeamsSeries ? 8 : 6,
                lineStyle: {
                    color: color,
                    width: isDeamsSeries ? 2.8 : 2,
                    opacity: isActive ? 1 : inactiveOpacity,
                },
                itemStyle: {
                    color: color,
                    borderColor: "#FFFFFF",
                    borderWidth: isDeamsSeries ? 1.5 : 0,
                    opacity: isActive ? 1 : inactiveOpacity,
                },
                areaStyle: hasSingleVisibleSeries && ((_a = echarts.graphic) === null || _a === void 0 ? void 0 : _a.LinearGradient)
                    ? {
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            { offset: 0, color: "".concat(color, "26") },
                            { offset: 1, color: "".concat(color, "05") },
                        ]),
                    }
                    : hasSingleVisibleSeries
                        ? { color: "".concat(color, "15") }
                        : undefined,
                emphasis: isDeamsSeries
                    ? {
                        focus: "series",
                        blurScope: "coordinateSystem",
                    }
                    : undefined,
                blur: isDeamsSeries
                    ? { lineStyle: { opacity: DEAMS_FOCUS_DIMMED_OPACITY } }
                    : undefined,
                data: dataPoints,
            };
        }
        return {
            name: name,
            type: "bar",
            color: color,
            stack: isStackedBar ? "total" : undefined,
            label: isClusteredBar ? buildBarValueLabel("top") : undefined,
            itemStyle: {
                color: color,
                borderRadius: [6, 6, 0, 0],
                borderColor: "#FFFFFF",
                borderWidth: isStackedBar ? 1 : 0,
            },
            data: dataPoints.map(function (value) {
                var _a;
                return ({
                    value: value,
                    itemStyle: {
                        color: ((_a = echarts.graphic) === null || _a === void 0 ? void 0 : _a.LinearGradient)
                            ? new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                { offset: 0, color: color },
                                { offset: 1, color: color + "AA" },
                            ])
                            : color,
                        borderRadius: [6, 6, 0, 0],
                        borderColor: "#FFFFFF",
                        borderWidth: isStackedBar ? 1 : 0,
                        opacity: selectedSeries.length === 0 || selectedSeries.includes(name)
                            ? 1
                            : isDeamsSeries
                                ? DEAMS_DIMMED_OPACITY
                                : DIMMED_OPACITY,
                    },
                });
            }),
            barMaxWidth: 28,
            emphasis: {
                itemStyle: {
                    shadowBlur: 10,
                    shadowColor: "rgba(0,0,0,0.12)",
                },
            },
        };
    });
    return {
        tooltip: multiTooltip,
        legend: {
            show: !useCustomLegendList && !isDeamsSeries,
            type: "scroll",
            orient: "vertical",
            right: 0,
            top: "middle",
            textStyle: {
                fontFamily: "Montserrat, sans-serif",
                fontSize: isMobile ? 10 : 11,
                color: LEGEND_TEXT_COLOR,
                rich: legendRichStyles,
            },
            itemWidth: !isMobile && !useCustomLegendList ? 0 : isMobile ? 10 : 12,
            itemHeight: !isMobile && !useCustomLegendList ? 0 : isMobile ? 10 : 12,
            itemGap: 12,
            formatter: legendFormatter,
        },
        grid: {
            left: isDeamsSeries ? "3%" : "3%",
            right: isDeamsSeries ? "2%" : "28%",
            bottom: isDeamsSeries ? "3%" : "8%",
            top: isDeamsSeries ? "6%" : isClusteredBar ? "14%" : "8%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: xValues,
            axisLabel: {
                fontFamily: "Montserrat, sans-serif",
                fontSize: isDeamsSeries ? 12 : 11,
                color: AXIS_TEXT_COLOR,
                interval: isDeamsSeries
                    ? xValues.length > 24
                        ? 2
                        : 1
                    : xValues.length <= 10
                        ? 0
                        : "auto",
                rotate: 0,
                formatter: function (value) {
                    return isDeamsSeries ? formatPeriodoLabel(value) : value;
                },
                margin: isDeamsSeries ? 18 : 8,
            },
            axisLine: {
                lineStyle: { color: isDeamsSeries ? "#9CA3AF" : "#E5E7EB" },
            },
            axisTick: { show: false },
        },
        yAxis: {
            type: "value",
            min: isDeamsSeries ? 0 : undefined,
            max: isDeamsSeries
                ? yAxisMax
                : isClusteredBar
                    ? getPaddedAxisMax(dataset.map(function (item) { return parseNumericValue(item[numericKey]); }))
                    : undefined,
            interval: isDeamsSeries ? yAxisStep : undefined,
            axisLine: { lineStyle: { color: isDeamsSeries ? "#9CA3AF" : "#E5E7EB" } },
            tickLine: { show: false },
            axisLabel: {
                fontFamily: "Montserrat, sans-serif",
                fontSize: isDeamsSeries ? 13 : 11,
                color: AXIS_TEXT_COLOR,
                formatter: function (value) { return formatNumericValue(value); },
            },
            splitNumber: isDeamsSeries ? ticks.length - 1 : undefined,
            axisTick: { show: false },
            width: isDeamsSeries ? 44 : undefined,
            splitLine: {
                lineStyle: {
                    color: isDeamsSeries ? "#D1D5DB" : "#F3F4F6",
                    type: "dashed",
                },
            },
            minInterval: isDeamsSeries ? yAxisStep : undefined,
            maxInterval: isDeamsSeries ? yAxisStep : undefined,
        },
        series: multiSeries,
    };
}
function tooltipFormatter(params) {
    var _a, _b, _c, _d, _e;
    if (Array.isArray(params)) {
        var axisLabel = (_d = (_b = (_a = params[0]) === null || _a === void 0 ? void 0 : _a.axisValueLabel) !== null && _b !== void 0 ? _b : (_c = params[0]) === null || _c === void 0 ? void 0 : _c.name) !== null && _d !== void 0 ? _d : "";
        if (params.length === 1) {
            return "<strong>".concat(axisLabel, "</strong><br/><strong>").concat(formatNumericValue((_e = params[0]) === null || _e === void 0 ? void 0 : _e.value), "</strong>");
        }
        var rows = params.map(function (item) {
            var _a, _b, _c;
            var marker = (_a = item.marker) !== null && _a !== void 0 ? _a : "";
            var seriesName = (_c = (_b = item.seriesName) !== null && _b !== void 0 ? _b : item.name) !== null && _c !== void 0 ? _c : "";
            return "".concat(marker).concat(seriesName, ": <strong>").concat(formatNumericValue(item.value), "</strong>");
        });
        return "<strong>".concat(axisLabel, "</strong><br/>").concat(rows.join("<br/>"));
    }
    return "<strong>".concat(params.name, "</strong><br/><strong>").concat(formatNumericValue(params.value), "</strong>");
}
